import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CommonLoader } from './CommonLoader'
import { Fab, Button, Tooltip, useTheme, IconButton } from '@mui/material'

export const CommonButton = ({
  id,
  label,
  width,
  startIcon,
  endIcon,
  iconOnly,
  loading = false,
  disabled = false,
  size = 'medium',
  color = 'primary',
  variant = 'primary',
  link = false,
  boxShadow = false,
  openInNewTab = false,
  to,
  style,
  toolTip,
  children,
  ...props
}) => {
  const theme = useTheme()
  const inputRef = useRef(null)
  const target = openInNewTab ? '_blank' : '_self'
  const rel = openInNewTab ? 'noopener noreferrer' : undefined

  const mapVariant = variant => {
    switch (variant) {
      case 'primary':
        return 'contained'
      case 'secondary':
        return 'outlined'
      case 'text':
        return undefined
      default:
        return 'contained'
    }
  }

  const handleButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const iconButtonStyles = useMemo(() => {
    const baseStyles = {
      boxShadow: boxShadow ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : '',
    }
    if (variant === 'secondary') {
      return {
        ...baseStyles,
        border: `1px solid ${theme.palette.primary.main}`,
      }
    }
    return baseStyles
  }, [variant, boxShadow, theme.palette.primary.main])

  const renderIconOnlyButton = () => {
    if (variant === 'primary') {
      return (
        <Fab
          id={id}
          data-testid={id}
          size={size}
          color={color}
          style={style}
          variant='circular'
          disabled={loading || disabled}
          sx={{
            boxShadow: boxShadow ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
          }}
          {...props}
        >
          {loading ? <CommonLoader size={16} /> : iconOnly}
        </Fab>
      )
    }
    return (
      <IconButton
        id={id}
        data-testid={id}
        size={size}
        color={color}
        style={style}
        sx={iconButtonStyles}
        disabled={loading || disabled}
        {...props}
      >
        {loading ? <CommonLoader size={16} /> : iconOnly}
      </IconButton>
    )
  }

  const renderLabeledButton = () => {
    const textDecoration = link ? 'underline' : 'none'
    const labelContent = (
      <span style={{ textDecoration }}>{children || label}</span>
    )
    return (
      <Button
        id={id}
        data-testid={id}
        size={size}
        color={color}
        style={style}
        onClick={handleButtonClick}
        variant={mapVariant(variant)}
        startIcon={loading ? <CommonLoader size={16} /> : startIcon}
        endIcon={!loading ? endIcon : undefined}
        disabled={loading || disabled}
        sx={{
          width,
          boxShadow:
            variant !== 'text' && !boxShadow ? '0px 2px 8px 0px #0000001A' : '',
        }}
        {...props}
      >
        {labelContent}
        {children &&
          React.isValidElement(children) &&
          React.cloneElement(children, { ref: inputRef })}
      </Button>
    )
  }

  const withLinkAndTooltip = element => {
    let result = element
    if (to) {
      result = (
        <Link to={to} target={target} rel={rel} disabled={disabled}>
          {result}
        </Link>
      )
    }
    if (toolTip) {
      result = (
        <Tooltip title={toolTip} arrow>
          {to ? result : <span>{result}</span>}
        </Tooltip>
      )
    }
    return result
  }

  const core = iconOnly ? renderIconOnlyButton() : renderLabeledButton()
  return withLinkAndTooltip(core)
}

CommonButton.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node,
  width: PropTypes.string,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  iconOnly: PropTypes.node,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
  link: PropTypes.bool,
  boxShadow: PropTypes.bool,
  openInNewTab: PropTypes.bool,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  style: PropTypes.object,
  toolTip: PropTypes.string,
  children: PropTypes.node,
}
